import { useTranslation } from 'react-i18next';
import { Bookmark, BookmarkCheck, Loader2 } from 'lucide-react';

import {
  useLinkRiotAccountMutation,
  useMyAccountsQuery,
  useUnlinkRiotAccountMutation,
} from '@/services/qriotsApi';
import type { IdentityGame, Platform } from '@/types/qriots';

interface SaveAccountButtonProps {
  puuid: string;
  game: IdentityGame;
  region: Platform;
  gameName: string;
  tagLine: string;
}

/**
 * Toggles the linked status of a Riot account for the current Telegram user.
 *
 * Uses myAccounts cache to know if (game, puuid) is already linked; toggles
 * link/unlink on click. Optimistically invalidates the MyAccounts tag so the
 * /riot/saved page refetches.
 */
export function SaveAccountButton(props: SaveAccountButtonProps) {
  const { t } = useTranslation('riot');
  const { data, isLoading: listLoading } = useMyAccountsQuery();
  const [link, linkResult] = useLinkRiotAccountMutation();
  const [unlink, unlinkResult] = useUnlinkRiotAccountMutation();

  const isLinked = !!data?.accounts.some(
    (a) => a.puuid === props.puuid && a.game === props.game,
  );
  const busy = listLoading || linkResult.isLoading || unlinkResult.isLoading;

  const onClick = () => {
    if (busy) return;
    if (isLinked) {
      unlink({ puuid: props.puuid, game: props.game });
    } else {
      link({
        game: props.game,
        gameName: props.gameName,
        tagLine: props.tagLine,
        region: props.region,
      });
    }
  };

  const label = isLinked ? t('cta.unsaveAccount') : t('cta.saveAccount');

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={[
        'mx-4 flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-opacity active:opacity-80',
        isLinked
          ? 'bg-bg-tertiary text-fg border border-success/30'
          : 'bg-success/15 text-fg border border-success/30',
        busy ? 'opacity-60' : '',
      ].join(' ')}
      aria-pressed={isLinked}
    >
      {busy ? (
        <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
      ) : isLinked ? (
        <BookmarkCheck aria-hidden className="h-4 w-4 text-success" />
      ) : (
        <Bookmark aria-hidden className="h-4 w-4" />
      )}
      <span>{label}</span>
    </button>
  );
}
